import { useState } from "react";
import { useSlotBlocks, useAppSettings, useEffectiveSlotConfig, type SlotBlock } from "@/lib/queries";
import { useCreateSlotBlock, useUpdateSlotBlock, useDeleteSlotBlock } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash, Plus } from "lucide-react";
import { todayIST } from "@emr/shared/india";
import { toast } from "sonner";
import { BlockRescheduleDialog, type BlockRescheduleMove } from "./BlockRescheduleDialog";

/**
 * Each slot_blocks row is an INDEPENDENT override (Phase 3 rework, 2026-10-18):
 *   - slot = a KEY of that date's effective config → controls only that slot
 *   - slot = null (whole day) → full block of every config slot
 * The create dialog picks a date, loads that date's effective config, and
 * offers "Whole day" + one button per config slot (label shown). The max
 * override is optional (0 = block, >0 = capacity restriction, empty =
 * inherit the slot def's max). The reason is admin-only at the API layer —
 * this card is scheduling.manage-gated, and the API nulls reasons for
 * non-admins, so the list renders reason ?? "—".
 */

function slotLabel(b: SlotBlock): string {
  if (b.slot === null) return "Whole day";
  return b.slot;
}

/** Scope of the override on THIS row's slot. */
function scopeLabel(b: SlotBlock): { kind: "block" | "restrict" | "inherit"; text: string } {
  if (b.slot === null) return { kind: "block", text: "Full block" };
  const max = b.maxOverride;
  if (max === 0) return { kind: "block", text: "Full block" };
  // NULL max = "inherit the slot def's max" — NOT a block.
  if (max === undefined || max === null) return { kind: "inherit", text: "Inherit slot max" };
  return { kind: "restrict", text: `Restrict to ${max}` };
}

export function BlockDaysCard({ canEdit }: { canEdit: boolean }) {
  const { data: blocksData } = useSlotBlocks();
  const { data: settings } = useAppSettings();
  const today = todayIST();
  // Past (completed) overrides are no longer actionable — hide them. Only
  // today and future overrides are listed (the current day IS included).
  // The rows stay in the DB (audit log + calendar history render them).
  const rows = ((blocksData?.rows ?? []) as SlotBlock[]).filter((b) => b.date >= today);

  // null = closed. "create" = Add dialog. "edit" = the SAME dialog pre-filled
  // for a fine edit of an existing row (whole-day rows included).
  const [dialog, setDialog] = useState<{ mode: "create" } | { mode: "edit"; block: SlotBlock } | null>(null);
  const [newDate, setNewDate] = useState("");
  // "" = whole day; otherwise a slot KEY of the picked date's config.
  const [newSlot, setNewSlot] = useState<string>("");
  const [newMax, setNewMax] = useState("");
  const [newReason, setNewReason] = useState("");
  // Stranded-appointment details returned by the server's must-move guard
  // (400 appointments_not_all_moved). Rendered inline so the user knows WHO
  // must be moved and where to do it.
  const [stranded, setStranded] = useState<{ id: string; patientName: string; status: string | null; slot: string | null }[]>([]);
  // Block Days reschedule flow (2026-10-29): {date, slot} scope the 400 came
  // for (slot "" = whole day). When set, the BlockRescheduleDialog opens —
  // one dialog that reschedules EVERY stranded appointment (auto-fill / AI)
  // and then retries the SAME block POST with appointmentsToMove.
  const [rescheduleScope, setRescheduleScope] = useState<{ date: string; slot: string | null } | null>(null);

  const create = useCreateSlotBlock();
  const del = useDeleteSlotBlock();
  const editing = dialog?.mode === "edit" ? dialog.block : null;
  const update = useUpdateSlotBlock(editing?.id ?? "");

  // The picked date's effective config — the scope selector offers its slots.
  const { slotList } = useEffectiveSlotConfig(newDate || undefined, settings);
  const slotLabelByKey = (key: string | null) => {
    if (key === null) return "Whole day";
    return slotList.find((s) => s.key === key)?.label ?? key;
  };

  const openCreate = () => {
    setDialog({ mode: "create" });
    setNewDate(""); setNewSlot(""); setNewMax(""); setNewReason("");
    setStranded([]);
  };

  const openEdit = (b: SlotBlock) => {
    setDialog({ mode: "edit", block: b });
    setNewDate(b.date);
    setNewSlot(b.slot ?? "");
    // maxOverride 0 = full block (max input blank); >0 = restriction.
    setNewMax(b.slot !== null && b.maxOverride != null && b.maxOverride > 0 ? String(b.maxOverride) : "");
    setNewReason(b.reason ?? "");
    setStranded([]);
  };

  // POST payload — shared by Add and by Edit-when-date/slot-changed (the
  // replacement row must pass the same server guards as a fresh block).
  // Phase 3 generic contract: { date, slot: key|null, block, reason }.
  // The POST schema still resolves the persisted max_override through the
  // legacy maxMorningAppts/maxEveningAppts channels (slotBlocks.ts (e)):
  //   - whole-day block → block:true alone pins max 0 (both channels 0)
  //   - Morning key     → maxMorningAppts
  //   - any other key   → maxEveningAppts (the non-Morning channel)
  // `block: true` + an explicit max wins for the row's own scope, so a
  // per-slot block sends max 0 on its channel and null on the other.
  const buildCreatePayload = () => {
    const slot = newSlot || undefined; // "" → undefined → whole day
    const maxVal = newMax.trim() === "" ? null : parseInt(newMax, 10);
    const payload: Record<string, unknown> = {
      date: newDate,
      slot,
      block: true,
      reason: newReason || null,
      appointmentsToMove: [],
    };
    if (slot === undefined) {
      // Whole day: block:true pins both channels to 0.
      payload.maxMorningAppts = 0;
      payload.maxEveningAppts = 0;
    } else if (slot === "Morning") {
      payload.maxMorningAppts = maxVal ?? 0;
      payload.maxEveningAppts = null;
    } else {
      // Evening AND custom keys: the non-Morning channel carries the value.
      payload.maxMorningAppts = null;
      payload.maxEveningAppts = maxVal ?? 0;
    }
    return payload;
  };

  const submit = async () => {
    if (!newDate) { toast.error("Pick a date"); return; }
    if (newDate < today) { toast.error("Pick today or a future date — past days are already completed"); return; }
    if (newSlot !== "" && newMax.trim() !== "") {
      const n = parseInt(newMax, 10);
      if (Number.isNaN(n) || n < 0) { toast.error("Max override must be 0 (block) or a positive number"); return; }
    }
    setStranded([]);
    try {
      if (dialog?.mode === "edit" && editing) {
        // Normalize both sides to null for whole-day ("" → null): a bare
        // `(newSlot || undefined) !== editing.slot` is ALWAYS true for
        // whole-day rows (undefined !== null) and would take the replace
        // path on every fine edit, 409-ing against the row itself.
        const slotChanged = (newSlot || null) !== (editing.slot ?? null);
        if (newDate === editing.date && !slotChanged) {
          // Fine edit of the SAME (date, slot) key → PATCH keeps the row id.
          // Generic maxOverride: null = inherit, 0 = block, >0 = restrict.
          const patch: Record<string, unknown> = { reason: newReason || null };
          if (editing.slot !== null) {
            patch.maxOverride = newMax.trim() === "" ? null : parseInt(newMax, 10);
          }
          await update.mutateAsync(patch);
        } else {
          // Date or slot changed → create the replacement FIRST (the server
          // re-runs the conflict + must-move guards on POST), then remove the
          // old row. If POST is refused the original override is untouched.
          await create.mutateAsync(buildCreatePayload());
          await del.mutateAsync(editing.id);
        }
      } else {
        await create.mutateAsync(buildCreatePayload());
      }
      setDialog(null);
    } catch (err) {
      // Block Days reschedule flow (2026-10-29): on the must-move guard 400,
      // open the reschedule dialog for the EXACT (date, slot) scope — one
      // dialog that proposes targets for every stranded appointment
      // (auto-fill / AI) and retries the SAME block POST with
      // appointmentsToMove. The inline stranded list remains as a fallback
      // when the dialog cannot open (no rows detail on the error).
      const e = err as Error & { body?: { error?: string; remainingDetails?: { id: string; patientName: string; status: string | null; slot: string | null }[] } };
      if (e?.body?.error === "appointments_not_all_moved") {
        if (e.body.remainingDetails?.length) {
          setStranded(e.body.remainingDetails);
          setRescheduleScope({ date: newDate, slot: newSlot || null });
          // No toast here — the mutation's onError already surfaces the
          // server message; the reschedule dialog opens on top of it.
        } else {
          setStranded([]);
        }
      }
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-serif text-lg text-maroon-700 dark:text-amber-500">Block Days & Slot Overrides</CardTitle>
        {canEdit && (
          <Button onClick={openCreate} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
            <Plus size={14} className="mr-1" /> Add override
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted">
          Each row is an independent override for one slot of that date's config (or a whole-day full block).
          Slots are controlled separately — blocking one never affects the others. Removing a row returns that
          slot to its config default. Past (completed) days are hidden — only today and upcoming overrides are listed.
        </p>
        {rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-cream-200 p-8 text-center text-sm text-muted dark:border-dark-700">
            No upcoming day-level overrides. Every working day uses the config defaults.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Slot</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Reason</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((b) => (
                <BlockRow
                  key={b.id}
                  block={b}
                  canEdit={canEdit}
                  today={today}
                  onEdit={() => openEdit(b)}
                  onDelete={() => del.mutate(b.id)}
                  deleting={del.isPending && del.variables === b.id}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialog !== null} onOpenChange={(v) => { if (!v) { setDialog(null); setStranded([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{dialog?.mode === "edit" ? "Edit slot override" : "Add slot override"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Date</Label>
              <Input type="date" min={today} value={newDate} onChange={(e) => { setNewDate(e.target.value); setNewSlot(""); setStranded([]); }} />
            </div>
            <div>
              <Label>Scope</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => { setNewSlot(""); setNewMax(""); }}
                  className={`rounded-lg border p-2 text-sm ${newSlot === "" ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 dark:border-dark-700"}`}
                >
                  Whole day
                </button>
                {slotList.map((def) => (
                  <button
                    key={def.key}
                    type="button"
                    onClick={() => setNewSlot(def.key)}
                    className={`rounded-lg border p-2 text-sm ${newSlot === def.key ? "border-maroon-600 bg-maroon-600/10" : "border-cream-200 dark:border-dark-700"}`}
                    title={`${def.startTime}–${def.endTime}`}
                  >
                    {def.label}
                  </button>
                ))}
              </div>
            </div>
            {newSlot !== "" && (
              <div>
                <Label>Max override (optional — 0 = block, empty = inherit slot max)</Label>
                <Input type="number" min={0} value={newMax} onChange={(e) => setNewMax(e.target.value)} placeholder={`Default: ${slotList.find((s) => s.key === newSlot)?.maxAppts ?? "—"}`} />
              </div>
            )}
            {newSlot === "" && (
              <p className="text-xs text-muted">
                Every slot of {newDate || "this date"} will be fully blocked. Existing confirmed appointments must be moved first.
              </p>
            )}
            {dialog?.mode === "edit" && (
              <p className="text-xs text-muted">
                Changing the date or slot replaces this override — the replacement must pass the block checks again
                (existing appointments on the new date/slot must be moved first).
              </p>
            )}
            {stranded.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-900/30">
                <div className="mb-2 font-medium text-amber-800 dark:text-amber-200">
                  {stranded.length} appointment(s) still scheduled for {newDate || "this date"} — move them first:
                </div>
                <ul className="mb-2 list-disc pl-5 text-xs text-amber-800 dark:text-amber-200">
                  {stranded.map((s) => (
                    <li key={s.id}>{s.patientName} · {s.slot ?? "no slot"} · {s.status}</li>
                  ))}
                </ul>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Reschedule these appointments in the dialog that opened (Auto-fill proposes targets; AI can refine — admin), then save — the block and the moves apply together.
                </p>
              </div>
            )}
            <div>
              <Label>Reason (optional)</Label>
              <Input value={newReason} onChange={(e) => setNewReason(e.target.value)} placeholder="e.g. Holiday, Doctor leave" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDialog(null); setStranded([]); }}>Cancel</Button>
            <Button onClick={submit} disabled={create.isPending || update.isPending || del.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              {dialog?.mode === "edit" ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Days reschedule flow (2026-10-29): opens when the block POST
          400s with stranded appointments. The dialog collects per-row move
          targets (auto-fill / AI / manual) and hands them back; the SAME
          atomic POST /slot-blocks (block payload + appointmentsToMove) then
          saves the block and moves every appointment in one guarded request.
          On success the card's lists refetch via the mutation's invalidation. */}
      <BlockRescheduleDialog
        open={rescheduleScope !== null}
        onOpenChange={(v) => { if (!v) { setRescheduleScope(null); setStranded([]); } }}
        date={rescheduleScope?.date ?? newDate}
        slot={rescheduleScope?.slot ?? null}
        onSubmit={async (moves) => {
          // Retry the SAME block POST with the moves attached — the SAME
          // buildCreatePayload() shape (whole day pins both channels 0;
          // a slot key pins its own channel; custom keys ride the Evening
          // channel — the non-Morning carrier).
          const scopeSlot = rescheduleScope?.slot ?? null;
          const payload: Record<string, unknown> = {
            date: rescheduleScope?.date ?? newDate,
            slot: scopeSlot ?? undefined,
            block: true,
            reason: newReason || null,
            appointmentsToMove: moves.map((m) => ({
              appointmentId: m.appointmentId,
              newDate: m.newDate,
              newSlot: m.newSlot,
              newArrivalTime: m.newArrivalTime,
            })),
          };
          const maxVal = newMax.trim() === "" ? null : parseInt(newMax, 10);
          if (scopeSlot === null) {
            payload.maxMorningAppts = 0;
            payload.maxEveningAppts = 0;
          } else if (scopeSlot === "Morning") {
            payload.maxMorningAppts = maxVal ?? 0;
            payload.maxEveningAppts = null;
          } else {
            payload.maxMorningAppts = null;
            payload.maxEveningAppts = maxVal ?? 0;
          }
          try {
            await create.mutateAsync(payload);
            setDialog(null);
            setStranded([]);
            setRescheduleScope(null);
            // No toast here — the mutation's onSuccess already toasts
            // "Override saved" and invalidates ["slot-blocks"] + ["appointments"].
          } catch (e) {
            const err = e as Error & { body?: { error?: string; message?: string } };
            throw err; // the dialog handles the preview refetch; the
                       // mutation's onError already surfaced the message.
          }
        }}
      />
    </Card>
  );
}

function BlockRow({ block, canEdit, today, onEdit, onDelete, deleting }: {
  block: SlotBlock;
  canEdit: boolean;
  today: string;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const isWholeDay = block.slot === null;
  const slot = slotLabel(block);
  const scope = scopeLabel(block);

  return (
    <TableRow>
      <TableCell className="font-medium">
        {block.date}
        {block.date === today && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800">Today</span>}
      </TableCell>
      <TableCell>
        <span className={`rounded px-2 py-0.5 text-xs ${isWholeDay ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>
          {slot}
        </span>
      </TableCell>
      <TableCell>
        <span className={`text-sm ${scope.kind === "block" ? "font-medium text-red-600" : "text-maroon-700 dark:text-amber-500"}`}>
          {scope.text}
        </span>
      </TableCell>
      {/* Reason is admin-only at the API layer (non-admin rows carry
          reason:null) — render exactly what the API sent, no fallbacks. */}
      <TableCell className="text-xs text-muted">{block.reason ?? "—"}</TableCell>
      {canEdit && (
        <TableCell className="text-right">
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
            <Button size="sm" variant="ghost" onClick={onDelete} disabled={deleting} title="Remove override">
              <Trash size={14} className="text-red-600" />
            </Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
